import { List, ListItem, ListItemText, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Alert } from '@mui/lab';
import * as api from "api/Knowledge";
import { GlobalClassKeys } from 'assets/jss/common-styles';
import BoxDrawer from 'components/BoxDrawer';
import IconButtonCancel from 'components/Buttons/IconButtonCancel';
import { LoadingIndicator } from 'components/CallServer/LoadingIndicator';
import { RequestErrorReport } from 'components/CallServer/RequestErrorReport';
import { GridEx } from 'components/MuiExtras/Grid/GridEx';
import MuiContainer from 'components/MuiExtras/MuiContainer';
import MuiPaper from 'components/MuiExtras/MuiPaper';
import MuiMuted from "components/MuiExtras/Typography/MuiMuted";
import { WithSnackbar, withSnackbar } from 'components/SnackbarProvider/Snackbar';
import { SubtitleItemDto } from 'models/ApiModels';
import * as React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { SearchBoxPlayer, VideoPlayerSearchApi, YoutubePlayerSearchApi } from 'views/Publication/Components/RenderVideo/RenderVideoCommon';

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IState {
    data: SubtitleItemDto[];
    lastError: any;
    loading: boolean;
    searchText: string;
    oldSearchText: string;
}

interface IProps {
    publicationId: string;
    fileId?: string;
    subtitle: string;
    onClose: () => void;
}

type Props = IProps & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

class PublicationSubtitlesDrawer extends React.PureComponent<Props, IState> {

    private searchRef: any = null;

    constructor(props: Props) {
        super(props);
        this.state = {
            data: null,
            lastError: null,
            loading: false,
            searchText: "",
            oldSearchText: ""
        };
    }

    private loadData() {
        if (this.state.searchText.length == 0) {
            this.setState({ data: null, lastError: null });
            return;
        }

        this.setState({ loading: true, data: null, lastError: null });

        const funcCall = this.props.fileId
            ? api.searchFileSubtitles(this.props.fileId, this.state.searchText)
            : api.searchMetadataSubtitles(this.props.publicationId, this.state.searchText);

        funcCall
            .then((data) => {
                this.setState({ data, loading: false, oldSearchText: this.state.searchText });
            })
            .catch((error) => {
                this.setState({ loading: false, lastError: error });
            });
    }

    ///
    private getPlayer(): SearchBoxPlayer {
        const card = document.getElementById(this.props.publicationId);
        if (card) {
            if (this.props.fileId != null) {
                const vp = card.getElementsByClassName(GlobalClassKeys.videoPlayer);
                if (vp.length == 1) return new VideoPlayerSearchApi(vp[0].querySelector("video").id);
            }
            else {
                const youtubeId = card.getElementsByClassName(GlobalClassKeys.youtubePlayer);
                if (youtubeId.length == 1) {
                    return new YoutubePlayerSearchApi(youtubeId[0].id);
                }
            }
        }
        return null;
    }

    ///
    private handleClick(startTime: number) {
        const api = this.getPlayer();

        if (api != null) {
            api.seek(startTime);
            api.play();
        }
    }

    ///
    private handleSearch(e: React.KeyboardEvent<HTMLDivElement>) {
        if (e.keyCode == 13) this.loadData();
    }


    ///
    private markText(text: string) {
        const { searchText } = this.state;
        if (text.length == 0 || searchText.length == 0) {
            return text;
        }

        // Destaca as palavras e/ou expressões.
        const reSplit = new RegExp('[\"].+?[\"]|[^ ]+', "ig");
        const words = searchText.match(reSplit);
        words.forEach((w) => {
            const w1 = w.replace(/\"/g, "");
            const regex = new RegExp(`(^|\\s)(${w1}[.,;!?"']?)(\\s|$)`, "ig");
            text = text.replace(regex, '$1<b>$2</b>$3');
        });
        return text.replace(/\n/g, "<br/>");
    }

    ///
    public componentDidMount() {
        this.loadData();
    }

    public render() {
        const { subtitle, onClose } = this.props;
        const { data, lastError, loading, searchText, oldSearchText } = this.state;

        return (
            <BoxDrawer
                open
                onClose={onClose}
                title={this.props.t("Views.Publication.Dialogs.VideoSearchSubtitlesDrawer.BoxTitle")}
                subtitle={subtitle}
            >

                <MuiPaper dense fullHeight elevation={0}>
                    <GridEx container direction="column" wrap="nowrap" fullHeight>

                        <GridEx>
                            <MuiContainer>
                                <TextField
                                    fullWidth
                                    autoFocus
                                    value={searchText}
                                    placeholder={this.props.t("Common.Input.Placeholder.SearchFor")}
                                    onChange={() => this.setState({ searchText: this.searchRef.value })}
                                    onKeyDown={(e) => this.handleSearch(e)}
                                    onBlur={() => { searchText != oldSearchText && this.loadData(); }}
                                    inputRef={(ref) => this.searchRef = ref}
                                    InputProps={
                                        {
                                            startAdornment: <SearchIcon />,
                                            endAdornment: this.state.searchText != ""
                                                ? <IconButtonCancel
                                                    size="small"
                                                    onClick={
                                                        () => {
                                                            this.setState({ searchText: "" }, () => this.loadData());
                                                        }
                                                    } />
                                                : null
                                        }
                                    }
                                />
                            </MuiContainer>
                        </GridEx>

                        <GridEx item xs overflow="auto">

                            {
                                lastError
                                    ? <RequestErrorReport error={lastError} />
                                    : loading
                                        ? <LoadingIndicator />
                                        : data == null
                                            ? <Alert severity="warning">{this.props.t("Views.Publication.Dialogs.VideoSearchSubtitlesDrawer.SearchEmpty")}</Alert>
                                            : data.length == 0
                                                ? <Alert severity="info">{this.props.t("Views.Publication.Dialogs.VideoSearchSubtitlesDrawer.ListEmpty")}</Alert>
                                                : (
                                                    <>
                                                        <List dense disablePadding>
                                                            {
                                                                data.map((v, index) => {
                                                                    return (
                                                                        <ListItem
                                                                            key={index}
                                                                            disableGutters
                                                                            className={GlobalClassKeys.clickable}
                                                                            onClick={() => this.handleClick(v.startTime)}
                                                                        >
                                                                            <ListItemText
                                                                                primary={<Typography variant="body2" component="div"><span dangerouslySetInnerHTML={{ __html: this.markText(v.lines) }}></span></Typography>}
                                                                                secondary={<Typography variant="body2" component="div"><MuiMuted><small>{v.timeRange}</small></MuiMuted></Typography>}
                                                                                primaryTypographyProps={{ noWrap: true }}
                                                                            />
                                                                        </ListItem>
                                                                    );
                                                                })
                                                            }
                                                        </List>
                                                    </>
                                                )
                            }
                        </GridEx>

                    </GridEx>
                </MuiPaper>
            </BoxDrawer>
        );
    }
}

export default withSnackbar(withTranslation()(PublicationSubtitlesDrawer));